import React from "react";
import {
  B2BSpinner,
  TravelerSelectWorkflow,
  LoadingIndicator,
  TravelerSelectStep,
  useDeviceTypes,
  hasInvalidBirthdate,
} from "halifax";
import {
  IPerson,
  PersonId,
  CallState,
  SELECT_TRAVELERS,
  ITravelerStepErrors,
} from "redmond";
import clsx from "clsx";

import "./styles.scss";
import { VacationRentalBookPassengerSelectionConnectorProps } from "./container";
import * as constants from "./textConstants";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import { config } from "../../../../api/config";
import { getConfigTenant, isVesperTenant, useIsTravelApp } from "@capone/common";

export interface IVacationRentalBookPassengerSelectionProps
  extends VacationRentalBookPassengerSelectionConnectorProps {
  progress: TravelerSelectStep;
  setProgress: (progress: TravelerSelectStep) => void;
  onProgressChange?: (step: TravelerSelectStep) => void;
  onContinue?: (travelersChanged?: boolean) => void;
  onGoBack?: (travelersChanged?: boolean) => void;
  // Called when the picker is closed by anything other than its own Back or
  // Continue, e.g. browser back in the travel app moving the checkout step.
  onDismiss?: (travelersChanged: boolean) => void;
  className?: string;
  selectionScreenHeaderElement?: JSX.Element;
  onReviewStep?: boolean;
  isMobile?: boolean;
  disabled?: boolean;
  combinedStep?: boolean;
  showErrors?: ITravelerStepErrors;
  setShowErrors?: (showErrors: ITravelerStepErrors) => void;
  saveButtonClicked?: boolean;
  useLocalId?: boolean;
  travelerToEdit?: IPerson;
  setTravelerToEdit?: (traveler?: IPerson) => void;
  preselectedUserId?: string;
  isXmodRedesign?: boolean;
}

export const VacationRentalBookPassengerSelection = (
  props: IVacationRentalBookPassengerSelectionProps
) => {
  const {
    className,
    travelers,
    progress,
    selectionScreenHeaderElement,
    setProgress,
    onProgressChange,
    onContinue,
    onGoBack,
    onDismiss,
    selectedTravelerId,
    setUserSelectedPassengerIds,
    updateUserPassenger,
    deleteUserPassenger,
    fetchUserPassengers,
    userPassengerCallState,
    onReviewStep,
    isMobile,
    disabled,
    combinedStep,
    showErrors,
    setShowErrors,
    saveButtonClicked,
    useLocalId,
    travelerToEdit,
    preselectedUserId,
    isXmodRedesign,
  } = props;
  const isVesper = isVesperTenant(getConfigTenant());

  const [localTravelerId, setLocalTravelerId] = React.useState<string[]>([]);
  const currentTravelerId = useLocalId
    ? localTravelerId[0]
    : selectedTravelerId;
  const travelersChanged = selectedTravelerId !== localTravelerId[0];
  const { matchesMobile } = useDeviceTypes();

  const { isTravelApp } = useIsTravelApp();

  React.useEffect(() => {
    fetchUserPassengers();
  }, []);

  React.useEffect(() => {
    if (preselectedUserId) {
      setLocalTravelerId([preselectedUserId]);
    }
  }, [preselectedUserId]);
  React.useEffect(() => {
    if (
      travelers.length === 1 &&
      !hasInvalidBirthdate(travelers[0].dateOfBirth)
    ) {
      setUserSelectedPassengerIds({
        userSelectedPassengerIds: [travelers[0].id],
      });
    }
  }, [travelers]);

  // Set when the picker closes itself (Back/Continue), so any other close can
  // be told apart and handled like Back.
  const closedByPicker = React.useRef(false);
  const previousProgress = React.useRef(progress);

  const handleContinue = () => {
    closedByPicker.current = true;
    if (useLocalId && travelersChanged) {
      setUserSelectedPassengerIds({
        userSelectedPassengerIds: localTravelerId,
      });
    }
    onContinue && onContinue(travelersChanged);
  };

  const handleGoBack = () => {
    closedByPicker.current = true;
    if (useLocalId && travelersChanged) {
      setUserSelectedPassengerIds({
        userSelectedPassengerIds: localTravelerId,
      });
    }
    onGoBack && onGoBack(travelersChanged);
  };

  React.useEffect(() => {
    const wasOpen = previousProgress.current !== TravelerSelectStep.Main;
    previousProgress.current = progress;
    if (progress !== TravelerSelectStep.Main) {
      closedByPicker.current = false;
      return;
    }
    if (!wasOpen || closedByPicker.current || !onDismiss) {
      return;
    }
    const hasPendingChange =
      !!useLocalId && localTravelerId.length > 0 && travelersChanged;
    if (hasPendingChange) {
      setUserSelectedPassengerIds({
        userSelectedPassengerIds: localTravelerId,
      });
    }
    onDismiss(hasPendingChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- react to the picker closing only
  }, [progress]);

  return userPassengerCallState === CallState.InProcess ? (
    <LoadingIndicator
      indicatorSize="small"
      indicator={B2BSpinner}
      message={
        userPassengerCallState === CallState.InProcess
          ? constants.UPDATE_TEXT
          : constants.VALIDATE_TEXT
      }
    />
  ) : (
    <TravelerSelectWorkflow
      displayModalSubtitle
      showGenderField
      showNationalityField
      singleTravelerWorkflow
      buttonClassName="b2b"
      className={clsx(
        "vacation-rental-book-passenger-selection-root",
        className,
        {
          "combined-step": combinedStep,
          redesigned: isVesper,
        }
      )}
      disabled={disabled}
      errorMessage={constants.ADD_TRAVELER_ERROR_MESSAGE}
      handleDeletePassenger={(travelerId: PersonId) => {
        if (useLocalId) {
          if (localTravelerId[0] === travelerId) {
            setLocalTravelerId([]);
          }
        }
        deleteUserPassenger({ personId: travelerId });
      }}
      handleUpdatePassenger={(traveler: IPerson) => {
        if (useLocalId) {
          setLocalTravelerId([traveler.id]);
        }
        updateUserPassenger(
          { person: traveler },
          traveler.driverLicense !== undefined
        );
      }}
      isMobile={matchesMobile}
      onContinue={handleContinue}
      onGoBack={handleGoBack}
      onProgressChange={onProgressChange}
      progress={progress}
      saveButtonClicked={saveButtonClicked}
      selectedTravelerIds={currentTravelerId ? [currentTravelerId] : []}
      selectionScreenHeaderElement={selectionScreenHeaderElement}
      setProgress={setProgress}
      setSelectedTravelerIds={(travelerIds: string[]) => {
        trackEvent({
          eventName: SELECT_TRAVELERS,
          properties: {},
        });
        if (useLocalId) {
          setLocalTravelerId(travelerIds);
        } else {
          setUserSelectedPassengerIds({
            userSelectedPassengerIds: travelerIds,
          });
        }
      }}
      setShowErrors={setShowErrors}
      showErrors={showErrors}
      titles={{
        travelerInfoTitle: onReviewStep
          ? constants.TRAVELER_INFO_REVIEW_TEXT
          : matchesMobile
          ? constants.TRAVELER_INFO_TEXT
          : constants.TRAVELER_INFO_TITLE_UPDATED,
        travelerInfoSubtitle: constants.TRAVELER_INFO_SUBTITLE,
        frequentFlyerTitle: constants.FREQUENT_FLYER_TITLE,
        additionalInfoTitle: constants.ADDITIONAL_INFO_TITLE,
        adultTitle: constants.ADULT_TITLE,
        childTitle: constants.CHILD_TITLE,
        infantSeatTitle: constants.INFANT_SEAT_TITLE,
        infantLapTitle: constants.INFANT_LAP_TITLE,
        addTravelers: matchesMobile
          ? onReviewStep
            ? constants.TRAVELER_INFO_TEXT
            : constants.ADD_TRAVELER_INFO_TEXT
          : constants.TRAVELER_INFO_TITLE_UPDATED,
      }}
      travelers={travelers}
      wrapButton={isMobile && !onReviewStep}
      editPassenger={travelerToEdit}
      tenant={config.TENANT}
      trackEvent={trackEvent}
      bottomContent={undefined}
      hideBackButton={isTravelApp}
      useXmodHeadings={isXmodRedesign}
    />
  );
};
